import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, Upload, ImageIcon, Building2, Phone, Mail, Globe, Plus, X } from "lucide-react";
import axios from "axios";
import { LabLetterheadSettings } from "@/components/LabLetterheadSettings";

/**
 * Branding Settings — super_admin only. Edits the global hospital identity
 * (name, tagline, address, phones, email, website) plus the logo image.
 * Changes here cascade to every page that reads /api/branding: sidebar,
 * kiosk, print pages, letterhead.
 */
export default function BrandingSettings() {
  const { user } = useAuth();
  const { reload, logo_url, hospital_id } = useBranding();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoV, setLogoV] = useState(Date.now());
  const fileRef = useRef(null);

  const canEdit = user?.role === "super_admin";

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/branding`).then((r) => setForm({
      name: r.data.name || "",
      short_name: r.data.short_name || "",
      tagline: r.data.tagline || "",
      address: r.data.address || "",
      phones: (r.data.phones && r.data.phones.length) ? r.data.phones : [""],
      email: r.data.email || "",
      website: r.data.website || "",
      abdm_enabled: !!r.data.abdm_enabled,
      show_doctor_sig_rx: !!r.data.show_doctor_sig_rx,
      show_patient_sig_bill: r.data.show_patient_sig_bill !== false,
      lab_signatory_name: r.data.lab_signatory_name || "",
      lab_signatory_qual: r.data.lab_signatory_qual || "",
      lab_preprinted: !!r.data.lab_preprinted,
      lab_top_margin_mm: r.data.lab_top_margin_mm ?? 40,
      lab_bottom_margin_mm: r.data.lab_bottom_margin_mm ?? 25,
    })).catch(() => toast.error("Failed to load branding"));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, phones: (form.phones || []).map((p) => p.trim()).filter(Boolean) };
      await api.put("/branding", payload);
      toast.success("Branding saved");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed — super_admin only");
    } finally { setBusy(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Choose an image file"); return; }
    if (file.size > 4 * 1024 * 1024) { toast.error("Logo must be under 4 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("token");
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/branding/logo`, fd, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      toast.success("Logo uploaded");
      setLogoV(Date.now());
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed — super_admin only");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (!form) return <div className="p-8 text-sm text-slate-500">Loading…</div>;
  const setPhone = (i, v) => { const next = [...form.phones]; next[i] = v; setForm({ ...form, phones: next }); };
  const addPhone = () => setForm({ ...form, phones: [...(form.phones || []), ""] });
  const rmPhone = (i) => { const next = form.phones.filter((_, k) => k !== i); setForm({ ...form, phones: next.length ? next : [""] }); };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Branding & Identity</h1>
          <p className="text-sm text-slate-500">These values appear on the sidebar, kiosk, letterhead, receipts and every printed slip.</p>
        </div>
        {!canEdit && <div className="text-xs bg-amber-50 text-amber-800 border border-amber-200 rounded-sm px-3 py-1.5">Read-only · super_admin required to save</div>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left · Logo */}
        <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
          <div className="label-tiny flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5"/> Logo</div>
          <div className="w-full aspect-[3/1] bg-slate-50 rounded-sm border border-slate-200 grid place-items-center p-4">
            <img key={logoV} src={`${logo_url}&_=${logoV}`} alt="Logo" className="max-h-32 object-contain"/>
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={uploadLogo} className="hidden" data-testid="brand-file"/>
          <Button
            variant="outline"
            disabled={!canEdit || uploading}
            onClick={() => fileRef.current?.click()}
            className="w-full rounded-sm"
            data-testid="brand-upload">
            <Upload className="w-4 h-4 mr-1"/> {uploading ? "Uploading…" : "Upload New Logo"}
          </Button>
          <div className="text-[11px] text-slate-500">PNG / JPG / WebP / SVG · up to 4 MB. The new logo appears everywhere after upload — no redeploy.</div>
        </div>

        {/* Right · Details */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-4 space-y-4">
          <div className="label-tiny flex items-center gap-1"><Building2 className="w-3.5 h-3.5"/> Hospital Identity</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-tiny">Name (Display)</Label>
              <Input value={form.name} disabled={!canEdit} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-9" data-testid="brand-name"/>
            </div>
            <div>
              <Label className="label-tiny">Short Name</Label>
              <Input value={form.short_name} disabled={!canEdit} onChange={(e) => setForm({ ...form, short_name: e.target.value })} className="mt-1 h-9" data-testid="brand-short-name"/>
            </div>
            <div className="col-span-2">
              <Label className="label-tiny">Tagline</Label>
              <Input value={form.tagline} disabled={!canEdit} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="MULTI SPECIALITY HOSPITAL" className="mt-1 h-9" data-testid="brand-tagline"/>
            </div>
            <div className="col-span-2">
              <Label className="label-tiny">Full Address</Label>
              <Textarea rows={2} value={form.address} disabled={!canEdit} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" data-testid="brand-address"/>
            </div>
            <div className="col-span-2">
              <Label className="label-tiny flex items-center gap-1"><Phone className="w-3 h-3"/> Contact Numbers</Label>
              <div className="space-y-1 mt-1">
                {form.phones.map((p, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <Input value={p} disabled={!canEdit} onChange={(e) => setPhone(i, e.target.value)} placeholder="+91 …" className="h-9 flex-1" data-testid={`brand-phone-${i}`}/>
                    {form.phones.length > 1 && canEdit && (
                      <Button size="sm" variant="outline" onClick={() => rmPhone(i)} className="h-9 w-9 p-0"><X className="w-4 h-4"/></Button>
                    )}
                  </div>
                ))}
                {canEdit && form.phones.length < 4 && (
                  <Button size="sm" variant="outline" onClick={addPhone} className="rounded-sm text-xs" data-testid="brand-add-phone"><Plus className="w-3 h-3 mr-1"/> Add another number</Button>
                )}
              </div>
            </div>
            <div>
              <Label className="label-tiny flex items-center gap-1"><Mail className="w-3 h-3"/> Email</Label>
              <Input value={form.email} disabled={!canEdit} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-9"/>
            </div>
            <div>
              <Label className="label-tiny flex items-center gap-1"><Globe className="w-3 h-3"/> Website</Label>
              <Input value={form.website} disabled={!canEdit} onChange={(e) => setForm({ ...form, website: e.target.value })} className="mt-1 h-9"/>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={save} disabled={!canEdit || busy} className="bg-blue-700 hover:bg-blue-800 rounded-sm" data-testid="brand-save">
              <Save className="w-4 h-4 mr-1"/> Save Branding
            </Button>
          </div>
        </div>
      </div>

      <LabLetterheadSettings form={form} setForm={setForm} hospitalId={user?.hospital_id || hospital_id} canEdit={canEdit} />

      {/* Modules toggle */}
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        <div className="label-tiny mb-2">Optional Modules</div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" data-testid="brand-abdm" disabled={!canEdit}
            checked={!!form.abdm_enabled}
            onChange={(e) => setForm({ ...form, abdm_enabled: e.target.checked })}
            className="w-5 h-5 accent-blue-700"/>
          <div>
            <div className="text-sm font-medium">Enable ABDM Center</div>
            <div className="text-[11px] text-slate-500">Shows the ABDM Center link in the sidebar. Off by default so staff don't see it until Ayushman Bharat integration is live.</div>
          </div>
        </label>
      </div>

      {/* Danger zone */}
      {canEdit && (
        <div className="bg-red-50 border border-red-300 rounded-sm p-4">
          <div className="label-tiny text-red-800 mb-2">Danger Zone</div>
          <div className="text-xs text-slate-700 mb-3">
            Permanently deletes <b>all</b> patients, doctors, OPDs, IPDs, Emergency, bills, payments and IPD sub-records.
            Users, branding, kiosk config and investigation master are preserved. Counters reset to 00001. There is no undo.
          </div>
          <Button
            variant="outline"
            className="border-red-400 text-red-700 hover:bg-red-100 rounded-sm"
            data-testid="brand-wipe"
            onClick={async () => {
              if (!window.confirm("This will DELETE ALL patients, doctors, OPDs, IPDs, bills and payments. Are you absolutely sure?")) return;
              if (!window.confirm("Last chance — this action cannot be undone. Continue?")) return;
              try {
                const { data } = await api.post("/admin/wipe-clinical-data");
                toast.success(`Wiped: ${Object.entries(data.deleted).map(([k, v]) => `${k}=${v}`).join(", ")}`);
              } catch (e) {
                toast.error(e.response?.data?.detail || "Wipe failed — super_admin only");
              }
            }}>
            Wipe All Clinical & Financial Data
          </Button>
        </div>
      )}
    </div>
  );
}
